import { useEffect, useState } from 'react';
import { useSAPDataStore } from '../store/SAPDataStore';
import { useAuth } from '../auth/useAuth';
import { NavigationHistoryService } from '../services/NavigationHistoryService';

/**
 * Simplified navigation history hook that uses the SAP data store
 * as the single source of truth for navigation history.
 */
export const useNavigationHistoryStore = () => {
  const { user } = useAuth();
  const {
    navigationHistory,
    currentUser,
    setCurrentUser,
    recordVisit,
    getNavigationHistory,
    setNavigationHistory,
    clearNavigationHistory
  } = useSAPDataStore();

  // True until we have loaded at least from localStorage
  const [loading, setLoading] = useState(true);

  // Initialize the navigation service and set current user
  useEffect(() => {
    NavigationHistoryService.initialize().catch(console.error);
    
    if (user?.username && user.username !== currentUser) {
      setCurrentUser(user.username);
    }
  }, [user?.username, currentUser, setCurrentUser]);

  // Load localStorage immediately (synchronous path), then sync NATS in background
  useEffect(() => {
    if (!user?.username) return;

    // Step 1: read localStorage right now — no async, no delay
    const localHistory = NavigationHistoryService.getLocalHistoryPublic(user.username);
    if (localHistory.length > 0) {
      const storeHistory = getNavigationHistory();
      // Merge local with anything already in the store
      const eventMap = new Map();
      localHistory.forEach(e => eventMap.set(`${e.type}-${e.id}`, e));
      storeHistory.forEach(e => {
        const key = `${e.type}-${e.id}`;
        const existing = eventMap.get(key);
        if (!existing || e.timestamp > existing.timestamp) eventMap.set(key, e);
      });
      const merged = Array.from(eventMap.values())
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 20);
      setNavigationHistory(merged);
    }
    setLoading(false);

    // Step 2: sync with NATS in background (non-blocking)
    const syncWithNATS = async () => {
      try {
        const natsHistory = await NavigationHistoryService.getHistory(user.username, 20);
        const storeHistory = getNavigationHistory();

        const eventMap = new Map();
        natsHistory.forEach(event => {
          const key = `${event.type}-${event.id}`;
          eventMap.set(key, event);
        });
        storeHistory.forEach(event => {
          const key = `${event.type}-${event.id}`;
          const existing = eventMap.get(key);
          if (!existing || event.timestamp > existing.timestamp) eventMap.set(key, event);
        });

        const mergedHistory = Array.from(eventMap.values())
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 20);

        if (JSON.stringify(mergedHistory) !== JSON.stringify(storeHistory)) {
          setNavigationHistory(mergedHistory);
          console.log(`🔄 Synced ${mergedHistory.length} navigation events from NATS`);
        }
      } catch (error) {
        console.warn('Failed to sync navigation history with NATS:', error);
      }
    };

    const timeoutId = setTimeout(syncWithNATS, 200);
    return () => clearTimeout(timeoutId);
  }, [user?.username, getNavigationHistory]);

  return {
    history: navigationHistory,
    loading,
    recordVisit,
    clearHistory: clearNavigationHistory,
    refresh: () => {
      if (user?.username) {
        NavigationHistoryService.getHistory(user.username, 20)
          .then(() => console.log('Navigation history refreshed'))
          .catch(error => console.warn('Failed to refresh navigation history:', error));
      }
    }
  };
};
